// Currency utility functions
export interface CurrencyConfig {
  code: string;
  symbol: string;
  name: string;
  position: 'before' | 'after';
  decimalPlaces: number;
}

// Currency mapping with symbols and formatting
export const CURRENCY_MAP: Record<string, CurrencyConfig> = {
  USD: {
    code: 'USD',
    symbol: '$',
    name: 'US Dollar',
    position: 'before',
    decimalPlaces: 2
  },
  EUR: {
    code: 'EUR',
    symbol: '€',
    name: 'Euro',
    position: 'after',
    decimalPlaces: 2
  },
  GBP: {
    code: 'GBP',
    symbol: '£',
    name: 'British Pound',
    position: 'before',
    decimalPlaces: 2
  },
  JPY: {
    code: 'JPY',
    symbol: '¥',
    name: 'Japanese Yen',
    position: 'before',
    decimalPlaces: 0
  },
  CAD: {
    code: 'CAD',
    symbol: 'C$',
    name: 'Canadian Dollar',
    position: 'before',
    decimalPlaces: 2
  },
  AUD: {
    code: 'AUD',
    symbol: 'A$',
    name: 'Australian Dollar',
    position: 'before',
    decimalPlaces: 2
  },
  INR: {
    code: 'INR',
    symbol: '₹',
    name: 'Indian Rupee',
    position: 'before',
    decimalPlaces: 2
  }
};

/**
 * Format currency value with proper symbol and positioning
 */
export const formatCurrency = (
  amount: number | string,
  currencyCode: string = 'USD'
): string => {
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  
  if (isNaN(numAmount)) {
    return '0.00';
  }

  const currency = CURRENCY_MAP[currencyCode] || CURRENCY_MAP.USD;
  const formattedAmount = numAmount.toFixed(currency.decimalPlaces);

  if (currency.position === 'before') {
    return `${currency.symbol}${formattedAmount}`;
  } else {
    return `${formattedAmount}${currency.symbol}`;
  }
};

/**
 * Get currency symbol only
 */
export const getCurrencySymbol = (currencyCode: string = 'USD'): string => {
  return CURRENCY_MAP[currencyCode]?.symbol || CURRENCY_MAP.USD.symbol;
};

/**
 * Get all available currencies
 */
export const getAvailableCurrencies = (): CurrencyConfig[] => {
  return Object.values(CURRENCY_MAP);
};

/**
 * Parse currency string back to number
 */
export const parseCurrency = (currencyString: string, currencyCode: string = 'USD'): number => {
  const currency = CURRENCY_MAP[currencyCode] || CURRENCY_MAP.USD;
  const cleanString = currencyString.replace(currency.symbol, '').replace(/,/g, '').trim();
  return parseFloat(cleanString) || 0;
};